import {
  Badge,
  Box,
  Divider,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Timeline,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { IconHistoryToggle } from '@tabler/icons-react';
import { ChangedByUser } from 'pages/claims/components';
import { getDisplayableClaimChanges } from 'pages/claims/components/ClaimHistoryTimeline';
import type {
  ClaimChange,
  ClaimHistory,
  ClaimHistoryActivity,
  Provider,
  Status,
} from 'interfaces';
import React from 'react';
import { ClaimTimelineItem } from 'pages/claims/components/ClaimHistoryTimeline';
import { compareDateDesc } from 'utils/dateUtils';
import { useTranslation } from '@refinedev/core';
import { ClaimHistoryActivityActions } from './ClaimHistoryActivityActions';

interface ClaimTimelineProps {
  data: ClaimHistory[];
  isLoading: boolean;
  isError: boolean;
  errorMessage: string;
  status: Status[];
  providers: Provider[];
}

const activityTitleFallbacks: Record<ClaimHistoryActivity['activity'], string> =
  {
    tariffCommentAdded: 'Tariff comment added',
    internalCommentAdded: 'Internal comment added',
    internalCommentRemoved: 'Internal comment deleted',
    claimQueried: 'Claim queried',
    supportingDocumentsSent: 'Supporting documents sent',
    submissionCancelled: 'Submission cancelled',
    claimLinked: 'Claim linked',
    claimUnlinked: 'Claim unlinked',
  };

export const ClaimTimeline: React.FC<ClaimTimelineProps> = ({
  data,
  isLoading,
  isError,
  errorMessage,
  status,
  providers,
}) => {
  const theme = useMantineTheme();
  const { translate } = useTranslation();
  const computedColorScheme = useComputedColorScheme('dark');

  const green = theme.colors.green[computedColorScheme === 'dark' ? 4 : 8];
  const red = theme.colors.red[computedColorScheme === 'dark' ? 4 : 8];
  const versionBadgeColor = computedColorScheme === 'dark' ? 'gray' : 'dark';

  if (isLoading) return <Loader />;
  if (isError) {
    return (
      <Text c="red">
        {errorMessage || 'An error occurred while fetching claim history.'}
      </Text>
    );
  }
  const displayableHistory = data
    .map((history) => ({
      ...history,
      changes: getDisplayableClaimChanges(history.changes),
    }))
    .filter((history) => history.changes.length > 0);
  if (displayableHistory.length === 0) {
    return <Text>No claim history available.</Text>;
  }
  const sortedHistory = displayableHistory.sort(
    (a: ClaimHistory, b: ClaimHistory) =>
      compareDateDesc(a.updatedAt, b.updatedAt)
  );

  const getFieldLabel = (field: string) => {
    switch (field) {
      case 'tariffs':
        return 'Tariffs';
      case 'debits':
        return 'Payments';
      case 'internalComments':
        return 'Internal note';
      case 'claimNumber':
        return 'Claim number';
      case 'statusID':
      case 'status':
        return 'Claim status';
      case 'providerID':
      case 'provider':
        return 'Provider';
      case 'patient':
      case 'patientID':
        return 'Patient';
      case 'healthcard':
      case 'healthcardID':
        return 'Health card';
      case 'payor':
      case 'payorID':
        return 'Payor';
      default:
        return field
          .replace(/ID$/, '')
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, (char) => char.toUpperCase());
    }
  };

  const getChangeCount = (changes: ClaimChange[]) =>
    changes.reduce((count, change) => {
      if (change.type === 'array') {
        const added = change.added?.length ?? 0;
        const removed = change.removed?.length ?? 0;
        const modified =
          change.modified?.reduce(
            (total, item) => total + (item.differences?.length ?? 1),
            0
          ) ?? 0;
        return count + added + removed + modified;
      }

      if (change.type === 'relation') {
        return count + Math.max(change.differences?.length ?? 1, 1);
      }

      return count + 1;
    }, 0);

  const getChangeSummary = (changes: ClaimChange[]) => {
    const count = getChangeCount(changes);
    if (count === 0) return 'No visible changes';
    if (count === 1) {
      return '1 field updated';
    }
    return `${count} fields updated`;
  };

  const HistoryTitle = ({ claimVersion }: { claimVersion: ClaimHistory }) => {
    const changesArray = getDisplayableClaimChanges(claimVersion.changes);
    const { changedBy, updatedAt } = changesArray[0] || {};
    const activity = changesArray.find((change) => change.type === 'activity');
    const updateTime = updatedAt || claimVersion.updatedAt;
    const visibleFields = Array.from(
      new Set(
        changesArray
          .filter((change) => change.type !== 'activity')
          .map((change) => getFieldLabel(change.field))
      )
    );
    const activityTitle =
      activity?.type === 'activity'
        ? translate(
            `pages:claims.claimHistory.activities.${activity.activity}`,
            activityTitleFallbacks[activity.activity]
          )
        : null;
    return (
      <Paper withBorder radius="md" shadow="sm">
        <Stack gap={0}>
          <Group justify="space-between" align="center" gap="sm" p="md">
            <Stack gap={8}>
              <Group gap="sm" wrap="nowrap">
                <Badge color={versionBadgeColor} radius="sm" size="lg">
                  {activityTitle ??
                    `Version ${claimVersion.version ?? 'unknown'}`}
                </Badge>
                <Text fw={700} size="sm">
                  {activityTitle ?? getChangeSummary(changesArray)}
                </Text>
              </Group>
              <ChangedByUser
                cognitoID={changedBy || 'auto'}
                updatedAt={updateTime}
                variant="compact"
              />
            </Stack>
            {visibleFields.length > 0 && (
              <Group gap={6} justify="flex-end">
                {visibleFields.slice(0, 3).map((field) => (
                  <Badge
                    key={field}
                    variant="light"
                    color="gray"
                    radius="sm"
                    tt="none"
                    size="lg"
                  >
                    {field}
                  </Badge>
                ))}
              </Group>
            )}
          </Group>
          <Divider />
          <Box p="md">
            {activity?.type === 'activity' && (
              <Box mb={changesArray.length > 1 ? 'md' : 0}>
                <ClaimHistoryActivityActions activity={activity} />
              </Box>
            )}
            <ClaimTimelineItem
              key={claimVersion.id}
              claimVersion={claimVersion}
              status={status}
              providers={providers}
              green={green}
              red={red}
            />
          </Box>
        </Stack>
      </Paper>
    );
  };

  return (
    <Timeline
      bulletSize={22}
      lineWidth={2}
      color="indigo"
      active={sortedHistory.length}
      w="100%"
    >
      {sortedHistory.map((claimVersion) => (
        <Timeline.Item
          key={claimVersion.id}
          radius="lg"
          bullet={
            <ThemeIcon radius="xl" size={22} color="indigo" variant="outline">
              <IconHistoryToggle size={12} />
            </ThemeIcon>
          }
          title={<HistoryTitle claimVersion={claimVersion} />}
        />
      ))}
    </Timeline>
  );
};
